import type { GatewayBrowserClient } from "../gateway.ts";
import type { LocalResourcesHistoryPoint, LocalResourcesSnapshot } from "../types.ts";

const HISTORY_MAX = 10;

export type LocalResourcesState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  localResourcesLoading: boolean;
  localResourcesSnapshot: LocalResourcesSnapshot | null;
  localResourcesError: string | null;
  localResourcesHistory: LocalResourcesHistoryPoint[];
};

function isUnknownResourcesMethod(err: unknown): boolean {
  return String(err).includes("unknown method: resources.local");
}

export function formatLocalResourcesError(err: unknown): string {
  if (isUnknownResourcesMethod(err)) {
    return "Local capacity needs a newer gateway. Run pnpm build, restart the gateway, and refresh.";
  }
  return String(err);
}

function appendHistory(
  history: LocalResourcesHistoryPoint[],
  snapshot: LocalResourcesSnapshot,
): LocalResourcesHistoryPoint[] {
  const point: LocalResourcesHistoryPoint = {
    collectedAtMs: snapshot.collectedAtMs,
    cpuPercent: snapshot.host.cpuPercent,
    memUsedPercent: snapshot.host.memUsedPercent,
  };
  return [...history, point].slice(-HISTORY_MAX);
}

export async function loadLocalResources(
  state: LocalResourcesState,
  opts?: { quiet?: boolean },
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.localResourcesLoading && !opts?.quiet) {
    return;
  }
  if (!opts?.quiet) {
    state.localResourcesLoading = true;
  }
  state.localResourcesError = null;
  try {
    const snapshot = await state.client.request<LocalResourcesSnapshot>("resources.local", {});
    state.localResourcesSnapshot = snapshot;
    state.localResourcesHistory = appendHistory(state.localResourcesHistory, snapshot);
  } catch (err) {
    state.localResourcesError = formatLocalResourcesError(err);
    if (!opts?.quiet) {
      state.localResourcesSnapshot = null;
    }
  } finally {
    if (!opts?.quiet) {
      state.localResourcesLoading = false;
    }
  }
}
